import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Plus, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { Task, TaskColumn } from "@/features/tasks/types/task";
import { TaskCard } from "@/features/tasks/components/TaskCard";
import { useState, useRef, useEffect } from "react";

interface KanbanColumnProps {
  column: TaskColumn;
  tasks: Task[];
  onAddTask: (columnId: string) => void;
  onSelectTask: (task: Task) => void;
  onEditTask: (task: Task) => void;
  onEditColumn: (column: TaskColumn) => void;
  onDeleteColumn: (columnId: string) => void;
  onCompleteTask: (task: Task) => void;
}

const colorAccents: Record<string, string> = {
  slate: "border-t-gray-400",
  blue: "border-t-blue-500",
  green: "border-t-green-500",
  amber: "border-t-amber-500",
  red: "border-t-red-500",
  purple: "border-t-purple-500",
  pink: "border-t-pink-500",
  indigo: "border-t-indigo-500",
};

const colorDot: Record<string, string> = {
  slate: "bg-gray-400",
  blue: "bg-blue-500",
  green: "bg-green-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  purple: "bg-purple-500",
  pink: "bg-pink-500",
  indigo: "bg-indigo-500",
};

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  tasks,
  onAddTask,
  onSelectTask,
  onEditTask,
  onEditColumn,
  onDeleteColumn,
  onCompleteTask,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const { setNodeRef, isOver } = useDroppable({ id: column.id });

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const accentClass = colorAccents[column.color] ?? "border-t-gray-400";
  const dotClass = colorDot[column.color] ?? "bg-gray-400";

  return (
    <div className={`flex flex-col w-72 min-w-[18rem] bg-gray-50 rounded-xl border-t-4 ${accentClass} shadow-sm`}>
      {/* Column header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${dotClass}`} />
          <h3 className="text-sm font-semibold text-gray-800 truncate max-w-[140px]">
            {column.label}
          </h3>
          <span className="text-xs text-gray-400 font-medium bg-gray-200 rounded-full px-1.5 py-0.5 leading-none">
            {tasks.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => onAddTask(column.id)}
            className="p-1 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
            title={t("tasks.column.add_task")}
          >
            <Plus className="w-4 h-4" />
          </button>
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onEditColumn(column);
                  }}
                  className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  {t("tasks.column.edit_title")}
                </button>
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onDeleteColumn(column.id);
                  }}
                  className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  {t("common.delete", { defaultValue: "Delete" })}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Task list */}
      <div
        ref={setNodeRef}
        className={`flex-1 p-2 flex flex-col gap-2 min-h-[120px] transition-colors ${
          isOver ? "bg-indigo-50" : ""
        }`}
      >
        <SortableContext
          items={tasks.map((t) => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onSelect={onSelectTask}
              onEdit={onEditTask}
              onComplete={onCompleteTask}
            />
          ))}
        </SortableContext>

        {tasks.length === 0 && (
          <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg min-h-[80px]">
            <p className="text-xs text-gray-400">{t("tasks.column.empty")}</p>
          </div>
        )}

        {/* Add task button at bottom */}
        <button
          onClick={() => onAddTask(column.id)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg px-2 py-1.5 transition-colors mt-1"
        >
          <Plus className="w-3.5 h-3.5" />
          {t("tasks.column.add_task")}
        </button>
      </div>
    </div>
  );
};
